import { useState, useRef, useEffect, useMemo } from "react";
import { MagnifyingGlass, CaretDown, Check, X as XIcon } from "@phosphor-icons/react";

/**
 * SearchableSelect — accessible dropdown with live text search.
 *
 * Props:
 *   options: Array<{ value: string, label: string, sublabel?: string, keywords?: string[] }>
 *   value: string | null   — current selected value
 *   onChange: (value) => void
 *   placeholder: string
 *   emptyText?: string     — text shown when no options match
 *   testid?: string        — data-testid prefix
 *   required?: boolean
 *   disabled?: boolean
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Seleziona...",
  emptyText = "Nessun risultato",
  testid = "searchable-select",
  required = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase().trim();
    return options.filter((o) => {
      const hay = [o.label, o.sublabel, ...(o.keywords || [])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Autofocus search input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => { setHighlight(0); }, [query, open]);

  const pick = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) pick(opt);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={`${testid}-wrapper`}>
      <button
        type="button"
        disabled={disabled}
        data-testid={`${testid}-trigger`}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full px-3 py-2 border border-neutral-300 text-sm text-left flex items-center justify-between gap-2 bg-white transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7]"}`}
      >
        <div className="flex-1 min-w-0">
          {selected ? (
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{selected.label}</span>
              {selected.sublabel && (
                <span className="text-xs text-neutral-500 truncate">· {selected.sublabel}</span>
              )}
            </div>
          ) : (
            <span className="text-neutral-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selected && !required && !disabled && (
            <span
              role="button"
              tabIndex={0}
              data-testid={`${testid}-clear`}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="p-0.5 hover:bg-neutral-200 transition-colors"
            >
              <XIcon size={14} />
            </span>
          )}
          <CaretDown size={14} className={`text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* Hidden native input to preserve required-form validation semantics */}
      {required && (
        <input
          type="text"
          tabIndex={-1}
          aria-hidden="true"
          required
          value={value || ""}
          onChange={() => {}}
          className="absolute inset-0 opacity-0 pointer-events-none"
        />
      )}

      {open && (
        <div
          className="absolute z-[60] mt-1 w-full bg-white border border-neutral-300 shadow-lg max-h-80 flex flex-col"
          data-testid={`${testid}-panel`}
        >
          <div className="flex items-center gap-2 border-b border-neutral-200 px-3 py-2 bg-neutral-50">
            <MagnifyingGlass size={14} className="text-neutral-400 shrink-0" />
            <input
              ref={inputRef}
              data-testid={`${testid}-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Cerca per codice, nome o produttore..."
              className="flex-1 outline-none text-sm bg-transparent"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="p-0.5 hover:bg-neutral-200 transition-colors">
                <XIcon size={12} />
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-neutral-500">{emptyText}</div>
            ) : (
              <ul>
                {filtered.map((o, i) => {
                  const isActive = o.value === value;
                  const isHi = i === highlight;
                  let itemBg = "hover:bg-neutral-50";
                  if (isActive) itemBg = "bg-[#002FA7]/10";
                  else if (isHi) itemBg = "bg-neutral-100";
                  return (
                    <li
                      key={o.value}
                      data-testid={`${testid}-option-${o.value}`}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => pick(o)}
                      className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 border-b border-neutral-50 last:border-b-0 transition-colors ${itemBg}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{o.label}</div>
                        {o.sublabel && (
                          <div className="text-xs text-neutral-500 truncate">{o.sublabel}</div>
                        )}
                      </div>
                      {isActive && <Check size={14} className="text-[#002FA7] shrink-0" />}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="px-3 py-1.5 text-[10px] text-neutral-500 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between">
            <span>{filtered.length} risultat{filtered.length === 1 ? "o" : "i"}</span>
            <span className="hidden sm:inline">↑↓ per navigare · ↵ per confermare · Esc chiude</span>
          </div>
        </div>
      )}
    </div>
  );
}
